import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarQuickFilter
} from '@mui/x-data-grid';
import {flexeMuiTheme} from '../../../shared/mui/default-mui-theme';
import {FlexeDataGrid} from '../../../shared/mui/flexe-data-grid/FlexeDataGrid';
import {EligibleForLoadShipment, FilterOptions, PaginationModel, SortOptions} from '../LoadInterfaces';
import ShipmentStatusMap from '../../outbound-shipments/ShipmentConstants';

interface SelectShipmentsTableProps {
  data: EligibleForLoadShipment[];
  isLoading: boolean;
  selectedShipmentIds: GridRowId[];
  rowSelectionModel: GridRowSelectionModel;
  handleSelectionModelChange: (selectedRows: GridRowSelectionModel) => void;
}

const DEFAULT_COLUMN_WIDTH = 100;
const DEFAULT_SHIPMENT_TABLE_PAGE_SIZE = 50;
const baseUrl = '/wh';

export function SelectShipmentsTable(props: SelectShipmentsTableProps) {
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: DEFAULT_SHIPMENT_TABLE_PAGE_SIZE
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Shipment ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: (params) => {
          const statusObject = ShipmentStatusMap.get(params.value);
          const className = `shipment-status-dot ${statusObject.className}`;
          return (
            <div>
              <span className={className} />
              {statusObject.text}
            </div>
          );
        },
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'purchase_order',
        headerName: 'Purchase Order',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'order_id',
        headerName: 'Order ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'destination_tag',
        headerName: 'Destination ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      }
    ],
    [baseUrl]
  );

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    const enablePurchaseOrderColumn = props.data.some(({purchase_order}) => purchase_order);
    const enableOrderIdColumn = props.data.some(({order_id}) => order_id);
    const enableDestinationColumn = props.data.some(({destination_tag}) => destination_tag);
    const model = {
      purchase_order: enablePurchaseOrderColumn,
      order_id: enableOrderIdColumn,
      destination: enableDestinationColumn
    };
    return model;
  }, [columns, props.data, props.isLoading]);

  return (
    <ThemeProvider theme={flexeMuiTheme}>
      <FlexeDataGrid
        autoHeight
        columns={columns}
        rows={props.data}
        rowCount={props.data?.length || 0}
        loading={props.isLoading}
        pageSizeOptions={[20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        columnVisibilityModel={columnVisibilityModel}
        sx={{
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {py: '8px'}
        }}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        slotProps={{panel: {disablePortal: true}}}
        checkboxSelection
        rowSelectionModel={props.rowSelectionModel}
        onRowSelectionModelChange={props.handleSelectionModelChange}
        keepNonExistentRowsSelected
        slots={{toolbar: CustomToolbar}}
      />
    </ThemeProvider>
  );

  // removes the export button but includes the quick filter
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarQuickFilter
          sx={{'border': 1, 'borderColor': '#C9D1DC', 'backgroundColor': '#fff', 'padding-bottom': 0}}
          debounceMs={250}
        />
      </GridToolbarContainer>
    );
  }
}
